import styled, { css, useTheme } from 'styled-components'

import { MediaQuery } from '@/@bothrs-ui/web/components/media-query/MediaQuery'
import { AppHeader } from '@/@bothrs-ui/web/components/navigation/header/AppHeader'
import { Line } from '@/@bothrs-ui/web/components/primitives/line/Line'
import { breakpoints } from '@/config/layout'

import type { ComponentPropsWithoutRef } from 'react'

/**
 * The shell of a detail screen; a page fills its rows.
 *
 * The title moves with the breakpoint: on a phone it sits in the Header under the back button, from
 * the M breakpoint it opens the content as a Title, so a page composes both with the same node.
 */

/* ------------------------------------------------------------------------------------------------- *
 * DetailPageLayout
 * ------------------------------------------------------------------------------------------------- */

type DetailPageLayoutRootProps = ComponentPropsWithoutRef<'div'>

const DetailPageLayoutRoot = ({ children, ...props }: DetailPageLayoutRootProps) => (
  <StRoot {...props}>{children}</StRoot>
)

/* ------------------------------------------------------------------------------------------------- *
 * DetailPageLayout.Header
 * ------------------------------------------------------------------------------------------------- */

// The phone's header row: the app header with its back action and, under it, the title. Hidden
// from the M breakpoint, where the title sits in the content instead. The gap below it equals the
// content gap, so the first content item sits at the same distance as every following one.
type DetailPageLayoutHeaderProps = ComponentPropsWithoutRef<'div'> & {
  /** Name of the back button (only its icon shows on a phone); the shell knows no i18n, so the page passes it. */
  backLabel: string
  onBack: () => void
}

const DetailPageLayoutHeader = ({ backLabel, onBack, children, ...props }: DetailPageLayoutHeaderProps) => {
  const theme = useTheme()

  return (
    <MediaQuery query={breakpoints.toM}>
      <StHeader {...props}>
        <AppHeader
          backgroundColor={theme.Colors.Background.White}
          primaryAction={{ label: backLabel, icon: ['far', 'chevron-left'], onClick: onBack }}
        />
        {children}
      </StHeader>
    </MediaQuery>
  )
}

/* ------------------------------------------------------------------------------------------------- *
 * DetailPageLayout.Title
 * ------------------------------------------------------------------------------------------------- */

// The title at the top of the content from the M breakpoint, with the line that closes it off.
// Placed by the page as the first item of its content, so it scrolls with it.
type DetailPageLayoutTitleProps = ComponentPropsWithoutRef<'div'>

const DetailPageLayoutTitle = ({ children, ...props }: DetailPageLayoutTitleProps) => (
  <MediaQuery query={breakpoints.fromM}>
    <StTitle {...props}>
      {children}
      <Line />
    </StTitle>
  </MediaQuery>
)

/* ------------------------------------------------------------------------------------------------- *
 * DetailPageLayout.Content
 * ------------------------------------------------------------------------------------------------- */

// The scrolling row. The row itself spans the shell so its scrollbar sits at the shell's edge; the
// centred column is an inner element. Stacking the children is up to what is placed inside it.
type DetailPageLayoutContentProps = ComponentPropsWithoutRef<'div'>

const DetailPageLayoutContent = ({ children, ...props }: DetailPageLayoutContentProps) => (
  <StContent {...props}>
    <StContentColumn>{children}</StContentColumn>
  </StContent>
)

/* ------------------------------------------------------------------------------------------------- *
 * DetailPageLayout.Footer
 * ------------------------------------------------------------------------------------------------- */

// The bar under the content, kept above the device's safe area.
type DetailPageLayoutFooterProps = ComponentPropsWithoutRef<'div'>

const DetailPageLayoutFooter = ({ children, ...props }: DetailPageLayoutFooterProps) => (
  <StFooter {...props}>{children}</StFooter>
)

/* ------------------------------------------------------------------------------------------------- *
 * Compound export
 * ------------------------------------------------------------------------------------------------- */

const DetailPageLayout = Object.assign(DetailPageLayoutRoot, {
  Header: DetailPageLayoutHeader,
  Title: DetailPageLayoutTitle,
  Content: DetailPageLayoutContent,
  Footer: DetailPageLayoutFooter,
})

export { DetailPageLayout }

/* ------------------------------------------------------------------------------------------------- *
 * Styled
 * ------------------------------------------------------------------------------------------------- */

// The width of the content itself from the M breakpoint up; the column's padding comes on top.
const CONTENT_WIDTH_PX = 620

// The page padding on a phone, and the centred column from the M breakpoint up. Shared by the content
// and footer rows so the constraint lives in one place.
const contentColumnCSS = css`
  padding: ${({ theme }) => theme.UI.SpacingPx.Space4} ${({ theme }) => theme.UI.SpacingPx.Space6};

  @media ${breakpoints.fromM} {
    margin: 0 auto;
    max-width: calc(${CONTENT_WIDTH_PX}px + 2 * ${({ theme }) => theme.UI.SpacingPx.Space6});
    width: 100%;
  }
`

const StRoot = styled.div`
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto;
  grid-template-areas:
    'header'
    'content'
    'footer';
  height: 100%;
  min-height: 0;
  overflow: hidden;
  background-color: ${({ theme }) => theme.Colors.Background.White};
`

// Mounted on a phone only, so it takes the phone padding and none of the column's desktop rules.
const StHeader = styled.div`
  grid-area: header;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.UI.SpacingPx.Space4};
  padding: ${({ theme }) => theme.UI.SpacingPx.Space4} ${({ theme }) => theme.UI.SpacingPx.Space6};
  padding-bottom: ${({ theme }) => theme.UI.SpacingPx.Space6};
`

// The same distance between the title and its line as between the content's sections.
const StTitle = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.UI.SpacingPx.Space6};
`

const StContent = styled.div`
  grid-area: content;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
`

const StContentColumn = styled.div`
  ${contentColumnCSS}
  padding-top: 0;
  padding-bottom: ${({ theme }) => theme.UI.SpacingPx.Space6};

  @media ${breakpoints.fromM} {
    padding-top: ${({ theme }) => theme.UI.SpacingPx.Space4};
  }
`

// The bar sits above the device's safe area, or above a minimum inset where there is none.
const StFooter = styled.div`
  ${contentColumnCSS}
  grid-area: footer;
  box-shadow: ${({ theme }) => theme.UI.Shadow.BottomBar};
  padding-bottom: calc(
    max(var(--safe-area-inset-bottom, 0px), ${({ theme }) => theme.UI.SpacingPx.Space5}) +
      ${({ theme }) => theme.UI.SpacingPx.Space1}
  );

  @media ${breakpoints.fromM} {
    box-shadow: none;
    padding-bottom: calc(
      max(var(--safe-area-inset-bottom, 0px), ${({ theme }) => theme.UI.SpacingPx.Space5}) +
        ${({ theme }) => theme.UI.SpacingPx.Space6}
    );
  }
`
